import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface LoadingSpinnerProps {
  size?: "sm" | "md" | "lg" | "xl";
  text?: string;
  fullScreen?: boolean;
  className?: string;
}

const sizeClasses = {
  sm: "h-4 w-4",
  md: "h-8 w-8",
  lg: "h-12 w-12",
  xl: "h-16 w-16"
};

export const LoadingSpinner = ({ 
  size = "md", 
  text, 
  fullScreen = false,
  className 
}: LoadingSpinnerProps) => {
  const spinner = (
    <div className={cn("flex flex-col items-center justify-center gap-3", className)}>
      <Loader2 className={cn("animate-spin text-primary", sizeClasses[size])} />
      {text && (
        <p className="text-sm text-muted-foreground animate-pulse">{text}</p>
      )}
    </div>
  );

  if (fullScreen) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        {spinner}
      </div>
    );
  }

  return spinner;
};

// Skeleton components for better loading states
export const ProductCardSkeleton = () => (
  <div className="space-y-3 animate-pulse">
    <div className="aspect-square bg-muted rounded-lg" />
    <div className="space-y-2">
      <div className="h-4 bg-muted rounded w-3/4" />
      <div className="h-4 bg-muted rounded w-1/2" />
      <div className="h-6 bg-muted rounded w-1/4 mt-2" />
    </div>
  </div>
);

export const OrderCardSkeleton = () => (
  <div className="border rounded-lg p-4 space-y-3 animate-pulse">
    <div className="flex justify-between">
      <div className="h-5 bg-muted rounded w-32" />
      <div className="h-5 bg-muted rounded w-24" />
    </div>
    <div className="space-y-2">
      <div className="h-4 bg-muted rounded w-full" />
      <div className="h-4 bg-muted rounded w-3/4" />
    </div>
    <div className="flex gap-2 pt-2">
      <div className="h-9 bg-muted rounded flex-1" />
      <div className="h-9 bg-muted rounded flex-1" />
    </div>
  </div>
);

export const ProfileSkeleton = () => (
  <div className="space-y-6 animate-pulse">
    <div className="flex items-center gap-4">
      <div className="h-20 w-20 rounded-full bg-muted" />
      <div className="space-y-2 flex-1">
        <div className="h-6 bg-muted rounded w-48" />
        <div className="h-4 bg-muted rounded w-32" />
      </div>
    </div>
    <div className="space-y-3">
      <div className="h-4 bg-muted rounded w-full" />
      <div className="h-4 bg-muted rounded w-5/6" />
      <div className="h-4 bg-muted rounded w-4/6" />
    </div>
  </div>
);
